/**
 * "카톡으로 물어보기" — OZK 도움연결 MVP entry point (P0.5, docs/OZK_FINAL_LAUNCH_STRATEGY.md 12장, 16장 ③A).
 *
 * This is NOT the same as the ③B "카카오톡으로 공유" button - this one creates
 * a help_request (POST /api/help-requests) and is about getting the user's
 * own question connected to OZK/a business, not about sharing content with
 * friends. Flow: question input -> submit -> reference code shown -> user
 * opens the OZK Kakao channel and continues the conversation there, quoting
 * the reference code (that code is the only link between this DB row and the
 * Kakao chat, since the two systems aren't otherwise connected).
 */
import { useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { useAuth } from "@/contexts/AuthContext";
import { useToast } from "@/hooks/use-toast";
import { apiRequest } from "@/lib/queryClient";
import { MessageCircleQuestion } from "lucide-react";

type HelpRequestSourceType =
  | "topic"
  | "article"
  | "business"
  | "market"
  | "community_talk"
  | "meeting"
  | "search";

interface HelpRequestButtonProps {
  sourceType: HelpRequestSourceType;
  sourceId?: string;
  topicId?: string;
  zoneId?: string;
  suburb?: string;
  variant?: "default" | "outline" | "ghost" | "secondary";
  size?: "sm" | "default" | "lg";
  className?: string;
  label?: string;
  /**
   * Topic-specific hint shown above the question box - e.g. a Topic's
   * helpConnection.handoffCopy (shared/topic-help-connection.ts). Optional:
   * most surfaces (article/business/market/meeting/search) don't have a
   * per-Topic contract, so this is only passed from TopicDetail.tsx.
   */
  recommendationCopy?: string;
}

// The real OZK 카카오톡 채널 URL isn't configured yet (16장 "현재 구현 상태" -
// this is intentionally an env var, not a hardcoded pf.kakao.com link, so we
// don't ship a fake channel URL before one actually exists).
const OZK_KAKAO_CHANNEL_URL = import.meta.env.VITE_OZK_KAKAO_CHANNEL_URL as
  | string
  | undefined;

export function HelpRequestButton({
  sourceType,
  sourceId,
  topicId,
  zoneId,
  suburb,
  variant = "outline",
  size = "default",
  className,
  label = "카톡으로 물어보기",
  recommendationCopy,
}: HelpRequestButtonProps) {
  const { user, isAuthenticated } = useAuth();
  const { toast } = useToast();
  const [open, setOpen] = useState(false);
  const [question, setQuestion] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [referenceCode, setReferenceCode] = useState<string | null>(null);

  const reset = () => {
    setQuestion("");
    setReferenceCode(null);
  };

  const handleSubmit = async () => {
    if (!isAuthenticated || !user) {
      toast({
        title: "로그인이 필요합니다",
        description: "카톡으로 물어보기는 로그인 후 이용하실 수 있습니다",
        variant: "destructive",
      });
      return;
    }
    if (!question.trim()) {
      toast({ title: "질문을 입력해주세요", variant: "destructive" });
      return;
    }

    setSubmitting(true);
    try {
      const res = await apiRequest("POST", "/api/help-requests", {
        question: question.trim(),
        sourceType,
        sourceId,
        topicId,
        zoneId,
        suburb,
      });
      const data = await res.json();
      setReferenceCode(data.referenceCode);
    } catch (error: any) {
      toast({
        title: "접수 실패",
        description: error?.message || "잠시 후 다시 시도해주세요",
        variant: "destructive",
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        if (!next) reset();
      }}
    >
      <Button
        type="button"
        variant={variant}
        size={size}
        className={className}
        onClick={() => setOpen(true)}
        data-testid="button-help-request"
      >
        <MessageCircleQuestion className="mr-1.5 h-4 w-4" />
        {label}
      </Button>
      <DialogContent className="sm:max-w-[480px]">
        {referenceCode ? (
          <>
            <DialogHeader>
              <DialogTitle>도움요청이 접수됐습니다</DialogTitle>
              <DialogDescription>
                아래 문의번호를 저장해두세요. 카카오톡 채널 대화에서 이 번호로 이어서 상담할 수 있습니다.
              </DialogDescription>
            </DialogHeader>
            <div className="rounded-lg border bg-muted/40 p-4 text-center">
              <p className="text-xs text-muted-foreground">문의번호</p>
              <p className="mt-1 text-2xl font-bold tracking-wide">{referenceCode}</p>
            </div>
            <DialogFooter className="flex-col gap-2 sm:flex-col">
              {OZK_KAKAO_CHANNEL_URL ? (
                <Button asChild className="w-full">
                  <a href={OZK_KAKAO_CHANNEL_URL} target="_blank" rel="noreferrer">
                    카카오톡 채널에서 계속 상담하기
                  </a>
                </Button>
              ) : (
                <p className="text-center text-sm text-muted-foreground">
                  카카오톡 채널 연결은 준비 중입니다. 접수하신 내용은 OZK 운영팀이 확인 후 순서대로 연락드립니다.
                </p>
              )}
              <Button type="button" variant="ghost" className="w-full" onClick={() => setOpen(false)}>
                닫기
              </Button>
            </DialogFooter>
          </>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle>카톡으로 물어보기</DialogTitle>
              <DialogDescription>
                OZK가 질문을 확인하고, 필요한 정보와 관련 업체를 안내해드립니다.
              </DialogDescription>
            </DialogHeader>
            {recommendationCopy && (
              <p className="rounded-md bg-muted/50 p-3 text-sm text-muted-foreground">
                {recommendationCopy}
              </p>
            )}
            <Textarea
              value={question}
              onChange={(e) => setQuestion(e.target.value)}
              placeholder="예: 회사가 두 달째 Super를 납부하지 않았어요. 어떻게 해야 하나요?"
              rows={5}
              maxLength={2000}
              data-testid="input-help-request-question"
            />
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setOpen(false)} disabled={submitting}>
                취소
              </Button>
              <Button type="button" onClick={handleSubmit} disabled={submitting}>
                {submitting ? "접수 중..." : "도움요청 보내기"}
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
